import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { LocaleService } from '../../../../core/services/locale.service';

export interface MenuItemAllergen {
  id: string;
  label: string;
}

/**
 * AllergenBadgesComponent — User-facing allergen display for menu items and cart.
 *
 * Shows allergen names in compact red/warning badges with overflow handling.
 * NOT the same as AllergenChipComponent (which is admin-only for editing).
 *
 * Usage:
 * ```html
 * <ct-allergen-badges [allergens]="item.allergens" [maxVisible]="3" />
 * ```
 *
 * Displays up to `maxVisible` allergens, then shows "+N more" for overflow.
 */
@Component({
  selector: 'ct-allergen-badges',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @for (allergen of visibleAllergens(); track allergen.id) {
      <span class="ab" [title]="containsLabel(allergen.label)">{{ formatAllergen(allergen.label) }}</span>
    }
    @if (overflowCount() > 0) {
      <span class="ab ab--more" [title]="overflowTooltip()">+{{ overflowCount() }}</span>
    }
  `,
  styles: [`
    :host {
      display: inline-flex;
      gap: 3px;
      flex-wrap: wrap;
    }

    .ab {
      font-size: 10px; /* type-scale floor */
      font-weight: 700;
      padding: 1px 5px;
      border-radius: 100px;
      background: color-mix(in srgb, var(--color-danger, #ef4444) 15%, transparent);
      color: var(--color-danger, #ef4444);
      line-height: 1.4;
      white-space: nowrap;
    }

    .ab--more {
      background: color-mix(in srgb, var(--color-danger, #ef4444) 20%, transparent);
      font-variant-numeric: tabular-nums;
    }
  `],
})
export class AllergenBadgesComponent {
  /** Array of allergens to display */
  readonly allergens = input.required<MenuItemAllergen[]>();

  /** Maximum number of allergen badges to show before "+N more" (default 3) */
  readonly maxVisible = input<number>(3);

  /** Allergens to show (limited by maxVisible) */
  protected readonly visibleAllergens = computed(() => {
    const max = this.maxVisible();
    return this.allergens().slice(0, max);
  });

  /** Number of allergens hidden in overflow */
  protected readonly overflowCount = computed(() => {
    const total = this.allergens().length;
    const max = this.maxVisible();
    return Math.max(0, total - max);
  });

  private readonly transloco = inject(TranslocoService);
  private readonly locale = inject(LocaleService);

  /** Tooltip text for "+N more" badge */
  protected readonly overflowTooltip = computed(() => {
    this.locale.locale();
    const overflow = this.allergens().slice(this.maxVisible());
    return this.transloco.translate('catering.customer.allergen_badges.also_contains', {
      list: overflow.map(a => a.label).join(', '),
    });
  });

  protected containsLabel(label: string): string {
    this.locale.locale();
    return this.transloco.translate('catering.customer.allergen_badges.contains', { allergen: label });
  }

  /** Format allergen label (capitalize first letter) */
  protected formatAllergen(label: string): string {
    return label.charAt(0).toUpperCase() + label.slice(1);
  }
}
